// Subscribe to raw OCR ws-server frames with optional filters.
//
// Mirrors the reference handler logic from
// ocr-ui/src/shared/tracking/TrackingManager.jsx and
// ocr-ui/src/modules/document-extraction/tools/ToolsView.jsx:
//
//   - `tableName`: drop frames whose `table_name` doesn't match. Optional.
//     Some `update` frames omit `table_name` entirely — those are passed
//     through when `lenient` is true (the reference does this for updates
//     but not inserts).
//   - `processId`: drop frames not addressed to this document. Useful in
//     the verification cockpit where each page is scoped to one process.
//   - `userScoped`: drop frames whose `id_details.user_id` is set and
//     doesn't match the logged-in user. Defaults to true. Frames without
//     a user_id are passed through (they're broadcast to everyone).
//
// The handler is stored in a ref so callers don't have to memoize it —
// only the filter options trigger a re-subscribe.

import { useEffect, useRef, useState } from "react";

import {
  acquireOcrSocket,
  releaseOcrSocket,
  type ConnectionState,
} from "../socket/ocrSocket";
import { getResolvedXUserId } from "../services/ocrApi";
import type { OcrTrackingFrame } from "../types/documents";

const OIDC_STORAGE_KEY =
  "oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app";

export interface UseOcrTrackingMessagesOptions {
  /** Filter to a specific backend table (e.g. "doc_extraction_tracking"). */
  tableName?: string;
  /** Filter to a specific process_id (or execution_id). */
  processId?: string;
  /** Drop frames meant for other users. Default true. */
  userScoped?: boolean;
  /**
   * If true (default), `update`/`process_complete` frames missing
   * `table_name` are passed through even when `tableName` is set. The
   * reference UI does this because some update signals omit the table.
   */
  lenientTableMatch?: boolean;
  /** Pause the subscription (e.g. while the route is hidden). */
  paused?: boolean;
}

export interface UseOcrTrackingMessagesResult {
  connected: boolean;
  error: string | null;
}

function readLoggedInUserId(): string | null {
  // Match the user_id the OCR backend stamps frames with. Resolution
  // order:
  //   1. The X-User-Id resolved via `/document_extraction/account`. This
  //      is the canonical OCR user id — same value the backend signs
  //      frames with. ocrApi.ts caches it at module scope.
  //   2. The legacy `sessionStorage.user.user_id` (ocr-ui's bootstrap
  //      path). Preserved for parity in case future code lands it there.
  //   3. The OIDC `sub` claim. Last resort — only matches when the
  //      backend hasn't been migrated to the resolved user_id yet.
  const resolved = getResolvedXUserId();
  if (resolved) return resolved;
  try {
    const raw = sessionStorage.getItem("user");
    if (raw) {
      const obj = JSON.parse(raw) as { user_id?: unknown };
      if (typeof obj.user_id === "string" && obj.user_id) return obj.user_id;
    }
  } catch {
    /* ignore */
  }
  try {
    const raw = localStorage.getItem(OIDC_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { profile?: { sub?: string } };
    return parsed?.profile?.sub ?? null;
  } catch {
    return null;
  }
}

function isFrame(value: unknown): value is OcrTrackingFrame {
  if (!value || typeof value !== "object") return false;
  const v = value as { action?: unknown };
  return typeof v.action === "string" && v.action.length > 0;
}

/**
 * Subscribe to OCR tracking frames matching the given filters. Calls
 * `onFrame` for every frame that passes the filters. Returns the live
 * connection state for "Live / Offline" badges.
 */
export function useOcrTrackingMessages(
  onFrame: (frame: OcrTrackingFrame) => void,
  options: UseOcrTrackingMessagesOptions = {},
): UseOcrTrackingMessagesResult {
  const {
    tableName,
    processId,
    userScoped = true,
    lenientTableMatch = true,
    paused = false,
  } = options;

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the latest handler in a ref so consumers don't have to memoize
  // it — we only re-subscribe when the filter options change.
  const handlerRef = useRef(onFrame);
  useEffect(() => {
    handlerRef.current = onFrame;
  }, [onFrame]);

  useEffect(() => {
    if (paused) return;

    const handle = acquireOcrSocket();
    const userId = userScoped ? readLoggedInUserId() : null;

    const unsubMsg = handle.onMessage((payload) => {
      if (!isFrame(payload)) return;

      // User scope: drop frames addressed to a different user. Frames
      // without a user_id are broadcasts — pass them through.
      if (userScoped && userId) {
        const sender = payload.id_details?.user_id;
        if (typeof sender === "string" && sender && sender !== userId) {
          return;
        }
      }

      // Table scope: strict for inserts (the reference treats inserts as
      // "new row appeared in table X" and refuses to misroute them); for
      // updates/completes, allow missing table_name when lenient mode is
      // on (some signals omit it).
      if (tableName) {
        const frameTable = payload.table_name;
        if (frameTable) {
          if (frameTable !== tableName) return;
        } else {
          const isUpdateLike =
            payload.action === "update" ||
            payload.action === "process_complete";
          if (!(lenientTableMatch && isUpdateLike)) return;
        }
      }

      // Process scope: filter to a specific document.
      if (processId) {
        const docId =
          payload.process_id ||
          payload.execution_id ||
          payload.id_details?.process_id ||
          payload.id_details?.execution_id;
        if (docId !== processId) return;
      }

      try {
        handlerRef.current(payload);
      } catch (err) {
        // Don't let consumer errors crash the subscription.
        // eslint-disable-next-line no-console
        console.error("[useOcrTrackingMessages] handler error:", err);
      }
    });

    const unsubState = handle.onState(
      (state: ConnectionState, err: string | null) => {
        setConnected(state === "open");
        setError(state === "open" ? null : err);
      },
    );

    return () => {
      unsubMsg();
      unsubState();
      releaseOcrSocket();
    };
  }, [tableName, processId, userScoped, lenientTableMatch, paused]);

  return { connected, error };
}
